/* =============================================================
   HeroSection — United Dream Mongolia (v4)
   - Landmarks blend into background, float across full screen
   - Restored CTA descriptions
   - Improved headline visual treatment
   ============================================================= */
import { motion } from "framer-motion";
import { ShoppingCart, Users, ArrowRight, GraduationCap, Handshake, Building2, MapPinned, Plane, Sparkles, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import CountrySelectModal from "@/components/CountrySelectModal";


/* ── Floating landmarks — full-screen drift paths ───────────── */
const LANDMARKS = [
  {
    emoji: "✈️",
    startX: "-5%",  startY: "20%",
    endX:   "105%", endY:   "15%",
    dur: 28, delay: 0, size: 56,
  },
  {
    emoji: "🌍",
    startX: "90%",  startY: "10%",
    endX:   "85%",  endY:   "80%",
    dur: 32, delay: 4, size: 60,
  },
  {
    emoji: "🗽",
    startX: "5%",   startY: "70%",
    endX:   "10%",  endY:   "15%",
    dur: 36, delay: 8, size: 50,
  },
  {
    emoji: "🏛️",
    startX: "105%", startY: "50%",
    endX:   "-5%",  endY:   "60%",
    dur: 30, delay: 2, size: 48,
  },
  {
    emoji: "🗼",
    startX: "50%",  startY: "-8%",
    endX:   "55%",  endY:   "105%",
    dur: 38, delay: 12, size: 46,
  },
  {
    emoji: "🌉",
    startX: "-5%",  startY: "85%",
    endX:   "105%", endY:   "75%",
    dur: 34, delay: 6, size: 54,
  },
  {
    emoji: "🏰",
    startX: "75%",  startY: "105%",
    endX:   "70%",  endY:   "-8%",
    dur: 40, delay: 16, size: 48,
  },
  {
    emoji: "🗺️",
    startX: "105%", startY: "30%",
    endX:   "-5%",  endY:   "25%",
    dur: 26, delay: 10, size: 50,
  },
  {
    emoji: "💵",
    startX: "20%",  startY: "105%",
    endX:   "25%",  endY:   "-8%",
    dur: 42, delay: 20, size: 52,
  },
];

function FloatingLandmarks() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
      {LANDMARKS.map((item, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ left: item.startX, top: item.startY, opacity: 0 }}
          animate={{
            left: [item.startX, item.endX],
            top:  [item.startY, item.endY],
            opacity: [0, 0.7, 0.7, 0],
          }}
          transition={{
            duration: item.dur,
            repeat: Infinity,
            ease: "linear",
            delay: item.delay,
            times: [0, 0.08, 0.92, 1],
          }}
        >
          {/* Violet-tinted icon tile */}
          <div
            style={{
              position: "relative",
              width:  item.size,
              height: item.size,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, rgba(124,58,237,0.22) 0%, rgba(79,70,229,0.14) 100%)",
              boxShadow: "0 0 20px rgba(124,58,237,0.2), inset 0 1px 0 rgba(167,139,250,0.15)",
            }}
          >
            {/* Emoji with violet hue-rotate + sepia to shift toward purple */}
            <span
              style={{
                fontSize: item.size * 0.5,
                lineHeight: 1,
                filter: "sepia(0.6) hue-rotate(220deg) saturate(1.8) brightness(0.85)",
                display: "block",
              }}
            >
              {item.emoji}
            </span>
            {/* Violet overlay to tint */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "16px",
                background: "rgba(124,58,237,0.28)",
                mixBlendMode: "color",
                pointerEvents: "none",
              }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ── Gradient orbs ───────────────────────────────────────────── */
function GradientOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 700, height: 700,
          background: "radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)",
          top: "-20%", left: "-10%",
        }}
        animate={{ x: [0, 55, 0], y: [0, 40, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 500, height: 500,
          background: "radial-gradient(circle, rgba(79,70,229,0.16) 0%, transparent 70%)",
          top: "30%", right: "-5%",
        }}
        animate={{ x: [0, -45, 0], y: [0, -55, 0] }}
        transition={{ duration: 17, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 320, height: 320,
          background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)",
          bottom: "12%", left: "32%",
        }}
        animate={{ x: [0, 35, 0], y: [0, -28, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut", delay: 6 }}
      />
    </div>
  );
}

/* ── Mesh grid ───────────────────────────────────────────────── */
function MeshGrid() {
  return (
    <motion.div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(rgba(124,58,237,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.05) 1px, transparent 1px)`,
        backgroundSize: "80px 80px",
      }}
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}

/* ── Cycling gradient for headline word ─────────────────────── */
const GRADS = [
  "linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #60a5fa 100%)",
  "linear-gradient(135deg, #c084fc 0%, #a78bfa 50%, #818cf8 100%)",
  "linear-gradient(135deg, #818cf8 0%, #60a5fa 50%, #a78bfa 100%)",
];

function GradWord({ children }: { children: React.ReactNode }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % GRADS.length), 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.span
      key={idx}
      initial={{ opacity: 0.5 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      style={{
        background: GRADS[idx],
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}
    >
      {children}
    </motion.span>
  );
}

/* ── CTA cards data ──────────────────────────────────────────── */
const EDUCATION_ACTIONS = [
  {
    id: "recommended-schools",
    icon: Sparkles,
    title: "Санал болгож буй сургууль",
    desc: "Хэлний оноотой, оноогүй 3 онцлох сургууль",
    isPrimary: false,
    isEducationSpotlight: true,
  },
  {
    id: "usa-schools",
    icon: Building2,
    title: "Онцлох сургуулиуд",
    desc: "АНУ, Канадын сургууль, хөтөлбөрөө харьцуул",
    isPrimary: false,
    isEducationSpotlight: true,
  },
  {
    id: "chicago-schools",
    icon: MapPinned,
    title: "Чикаго сургуулиуд",
    desc: "30+ сургууль, төлбөр ба амьдралын мэдээлэл",
    isPrimary: false,
    isEducationSpotlight: true,
  },
  {
    id: "early-college",
    icon: GraduationCap,
    title: "2+2 College",
    desc: "16 наснаас эхэлж, 20 насандаа бакалаврын зэргээ авна",
    isPrimary: false,
    isEducationSpotlight: true,
  },
];

const SERVICE_ACTIONS = [
  {
    id: "dv-check",
    icon: ShieldCheck,
    title: "АНУ Green Card / DV Lottery",
    desc: "АНУ-д байнгын оршин суух боломжоо шалгах онцгой үйлчилгээ",
    isPrimary: false,
    isDVSpotlight: true,
  },
  {
    id: "visa",
    icon: ShoppingCart,
    title: "Виз мэдүүлэх",
    desc: "АНУ, Австрали, Канад, Шенген болон бусад улсад онлайнаар мэдүүл",
    isPrimary: true,
  },
  {
    id: "chicago-tour",
    icon: Plane,
    title: "Чикаго Christmas аялал",
    desc: "12 сар · 7 хоногийн цастай баярын аялал · $3,990",
    isPrimary: false,
    isChicagoTour: true,
  },

  {
    id: "franchise",
    icon: Handshake,
    title: "Франчайз",
    desc: "UDM-ийн дэмжлэгтэй визний зөвлөгөөний бизнесээ эхлүүл",
    isPrimary: false,
    isFranchise: true,
  },
];

export const CORE_ACTIONS = [...EDUCATION_ACTIONS, ...SERVICE_ACTIONS];

export default function HeroSection() {


  const [, navigate] = useLocation();
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  const handleAction = (id: string) => {
    if (id === "visa") setCountryModalOpen(true);
    else if (id === "recommended-schools") navigate("/recommended-schools");
    else if (id === "usa-schools") navigate("/schools");
    else if (id === "chicago-schools") navigate("/study/chicago");
    else if (id === "consult") navigate("/consultation");
    else if (id === "dv-check") navigate("/dv/check");
    else if (id === "chicago-tour") navigate("/chicago-tour");
    else if (id === "early-college") navigate("/early-college");
    else if (id === "franchise") navigate("/franchise");
  };

  const renderActionCard = (action: (typeof CORE_ACTIONS)[number], i: number, education = false) => {
    const Icon = action.icon;
    return (
      <motion.button
        key={action.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.42 + i * 0.07 }}
        whileHover={{ y: -4, scale: 1.025 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => handleAction(action.id)}
        className={`group relative flex flex-col items-start gap-3 rounded-2xl p-5 text-left transition-all duration-300 ${education ? "min-h-[172px]" : ""}`}
        style={
          action.isPrimary
            ? {
                background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                boxShadow: "0 8px 32px rgba(124,58,237,0.45), 0 2px 8px rgba(0,0,0,0.3)",
                border: "1px solid rgba(124,58,237,0.5)",
              }
            : (action as any).isEducationSpotlight
            ? {
                background: "linear-gradient(135deg, rgba(3,105,161,0.88) 0%, rgba(30,111,217,0.84) 48%, rgba(8,145,178,0.78) 100%)",
                border: "1px solid rgba(186,230,253,0.78)",
                boxShadow: "0 12px 32px rgba(14,165,233,0.32), inset 0 1px 0 rgba(255,255,255,0.18)",
              }
            : (action as any).isDVSpotlight
            ? {
                background: "linear-gradient(135deg, #166534 0%, #059669 52%, #15803D 100%)",
                border: "1px solid rgba(187,247,208,0.92)",
                boxShadow: "0 14px 36px rgba(16,185,129,0.44), inset 0 1px 0 rgba(255,255,255,0.22)",
              }
            : (action as any).isChicagoTour
            ? {
                background: "linear-gradient(135deg, rgba(127,29,29,0.58) 0%, rgba(153,27,27,0.32) 54%, rgba(22,101,52,0.30) 100%)",
                border: "1px solid rgba(253,230,138,0.56)",
                boxShadow: "0 8px 24px rgba(220,38,38,0.20)",
              }
            : (action as any).isFranchise
            ? {
                background: "linear-gradient(135deg, rgba(245,158,11,0.20) 0%, rgba(124,58,237,0.20) 100%)",
                border: "1px solid rgba(251,191,36,0.42)",
                boxShadow: "0 8px 24px rgba(245,158,11,0.14)",
              }
            : {
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.09)",
                backdropFilter: "blur(12px)",
              }
        }
      >
        {!action.isPrimary && (
          <div
            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{ background: (action as any).isChicagoTour ? "linear-gradient(135deg, rgba(254,240,138,0.16) 0%, rgba(255,255,255,0.06) 100%)" : (action as any).isDVSpotlight ? "linear-gradient(135deg, rgba(187,247,208,0.24) 0%, rgba(255,255,255,0.08) 100%)" : (action as any).isEducationSpotlight ? "linear-gradient(135deg, rgba(125,211,252,0.16) 0%, rgba(255,255,255,0.06) 100%)" : "linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(79,70,229,0.07) 100%)" }}
          />
        )}
        <div className="relative flex items-center justify-between w-full">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={
              action.isPrimary
                ? { background: "rgba(255,255,255,0.18)" }
                : (action as any).isEducationSpotlight
                ? { background: "rgba(224,242,254,0.18)", border: "1px solid rgba(224,242,254,0.36)" }
                : (action as any).isDVSpotlight
                ? { background: "rgba(220,252,231,0.20)", border: "1px solid rgba(220,252,231,0.45)" }
                : (action as any).isChicagoTour
                ? { background: "rgba(254,240,138,0.18)", border: "1px solid rgba(253,230,138,0.36)" }
                : { background: "rgba(124,58,237,0.14)", border: "1px solid rgba(124,58,237,0.22)" }
              }
            >
            {Icon && <Icon className={`w-5 h-5 ${action.isPrimary ? "text-white" : (action as any).isEducationSpotlight ? "text-sky-100" : (action as any).isDVSpotlight ? "text-emerald-50" : (action as any).isChicagoTour ? "text-amber-100" : "text-violet-400"}`} />}
          </div>
          <ArrowRight className={`w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 ${action.isPrimary ? "text-white" : (action as any).isEducationSpotlight ? "text-sky-100" : (action as any).isDVSpotlight ? "text-emerald-50" : (action as any).isChicagoTour ? "text-amber-100" : "text-violet-400"}`} />
        </div>
        <div className="relative">
          {(action as any).isDVSpotlight && <span className="mb-1 inline-flex rounded-full border border-emerald-100/70 bg-emerald-100/20 px-2 py-0.5 text-[9px] font-extrabold tracking-[0.14em] text-emerald-50">ОНЦГОЙ</span>}
          <div className={`font-bold text-sm leading-tight mb-1 ${action.isPrimary ? "text-white" : (action as any).isEducationSpotlight || (action as any).isChicagoTour || (action as any).isDVSpotlight ? "text-white" : "text-white/80"}`} style={{ fontFamily: "'Sora', var(--font-display), sans-serif" }}>
            {action.title}
          </div>
          <div className="text-[11px] leading-snug" style={{ color: action.isPrimary ? "rgba(255,255,255,0.68)" : (action as any).isEducationSpotlight ? "rgba(240,249,255,0.88)" : (action as any).isDVSpotlight ? "rgba(236,253,245,0.94)" : (action as any).isChicagoTour ? "rgba(254,242,242,0.90)" : "rgba(255,255,255,0.36)", fontFamily: "var(--font-body)" }}>
            {action.desc}
          </div>
        </div>
      </motion.button>
    );
  };

  return (
    <section
      id="home"
      className="relative min-h-screen overflow-hidden flex flex-col justify-center"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 75%, #0A0820 100%)" }}
    >
      <GradientOrbs />
      <MeshGrid />
      <FloatingLandmarks />

      {/* Bottom glow line */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(124,58,237,0.7), rgba(79,70,229,0.7), transparent)" }}
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container relative z-10 pt-24 pb-16">
        <div className="max-w-3xl mx-auto text-center">

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-10"
            style={{
              background: "rgba(124,58,237,0.1)",
              border: "1px solid rgba(124,58,237,0.3)",
              backdropFilter: "blur(10px)",
            }}
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-violet-300 text-[11px] font-semibold tracking-[0.18em] uppercase">
              Визийн зуучлал &amp; зөвлөх үйлчилгээ
            </span>
          </motion.div>



          {/* ── Education choices — prioritized above general services ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mb-4"
          >
            <div className="mb-3 flex items-center justify-center gap-2 text-xs font-bold tracking-[0.14em] text-sky-200 uppercase">
              <GraduationCap className="h-4 w-4" />
              АНУ-д суралцах боломжууд
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {EDUCATION_ACTIONS.map((action, i) => renderActionCard(action, i, true))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
          >
            {SERVICE_ACTIONS.map((action, i) => renderActionCard(action, i + EDUCATION_ACTIONS.length))}
          </motion.div>

        </div>
      </div>

      <CountrySelectModal isOpen={countryModalOpen} onClose={() => setCountryModalOpen(false)} />

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, rgba(13,10,46,0.7))" }}
      />
    </section>
  );
}
